
import type { StudentCountsInShapeColorByEnum } from "@/providers/store/api/shapes/enums/student-counts-in-shape.enum";
import type {
  IAdvancedFilter,
  IClusterGridKey,
  ISchoolCountsInShapeResult,
  IStudentCountsInShapeResult,
} from "@/providers/store/api/shapes/interfaces/shapes.interface";
import type { VisibleSchoolTypesInShape } from "@/providers/store/api/shapes/types/visible-school-types-in-shape.types";
import type { StudentImportType } from "@/providers/store/api/students/students.interface";

import { type SystemUserReportTypeEnum } from "./system-user-report-type.enum";

import type { MultiPolygon, Polygon } from "geojson";

export type ReportJobStatusEnum = "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED";

export enum ReportExportFormat {
  EXCEL = "excel",
  PDF = "pdf",
  CSV = "csv",
}

export interface IExportReportArg {
  reportId: string;
  format: ReportExportFormat;
}

export interface IExportCustomReportArg {
  customReportId: string;
  format: ReportExportFormat;
  districtId?: string | null;
}

export interface IExportReportResult {
  blob: Blob;
  filename: string;
}

export interface IExportReportSuccessResult {
  success: true;
  filename: string;
}

export interface IFoundersLetterQrResponse {
  dataUrl: string;
}

export type ProjectionTypeEnum =
  | "district-wide"
  | "all-schools"
  | "elementary"
  | "middle"
  | "high"
  | "k-8"
  | "individual";

export type ReportValueTypeEnum = "MODERATE" | "CONSERVATIVE";

export interface IReportJobError {
  message: string;
  code?: string;
  details?: unknown;
  schoolId?: string;
}

export interface IReportJobProjectionStepStatus {
  id: string;
  projectionApiSlug: string;
  projectionModelSlug: string;
  projectionModelId: string;
  projectionModelName: string;
  status: "PENDING" | "IN_PROGRESS" | "COMPLETED" | "FAILED";
  processedSchools: number;
  totalSchools: number;
  errors?: IReportJobError[];
  updatedAt: string;
}

export interface IReportJobStatusResponse {
  status: ReportJobStatusEnum | null;
  hasJob: boolean;
  errors?: IReportJobError[];
  createdAt?: string;
  updatedAt?: string;
  snapshotType: "FALL_SNAPSHOT" | "MONTHLY";
  steps?: IReportJobProjectionStepStatus[];
}

export interface IProjectionModelMetadata {
  id: string;
  slug: string;
  name: string;
  api: { id: string; slug: string; name: string };
}

export interface IProjectionEngine {
  id: "legacy" | "per_district";
  name: string;
  isLegacy: boolean;
  available: boolean;
  disabledReason?: string;
}

export interface IProjectionApisResponse {
  engines: IProjectionEngine[];
  defaultIsLegacy: boolean | null;
}

export interface IDistrictProjectionDefaultResponse {
  districtId: string;
  defaultIsLegacy: boolean | null;
}

export interface IDistrictMlTrainingError {
  code?: string;
  message?: string;
  retryable?: boolean;
  failed_stage?: string;
  failedStage?: string;
}

export interface IDistrictMlTrainingStatusResponse {
  districtId: string;
  projectionKey: string | null;
  status: string | null;
  trainingRunId: string | null;
  hasCompletedTraining: boolean;
  error: IDistrictMlTrainingError | string | null;
  finishedAt: string | null;
}

export interface IProjectionTableData {
  schoolId?: string;
  tableName: string;
  startYear: number;
  yearsToPredict: number;
  /** `isEstimate` (projection tables only): TK is an ML estimate, outside every K–12 total (EC-1262). */
  grades: { grade: string; values: number[]; isEstimate?: boolean }[];
  subtotals?: { values: number[] };
  pctChg?: { values: number[] };
  sdc?: { values: number[] };
  totals: { values: number[] };
  capacity?: { values: number[] };
  openSeats?: { values: number[] };
  /** Projected + Estimated Total (TK–12); only on projection tables with a TK estimate row (EC-1262). */
  estimatedTotals?: { values: number[] };
  yearsClosedInRange?: number;
  comment?: string;
}

export interface IAggregatedProjectionResponse {
  tables: IProjectionTableData[];
  /** Same header text the individual report shows once saved. */
  description?: string;
}

export interface IGetAggregatedReportArg {
  districtId: string;
  reportType: ProjectionTypeEnum;
  valueType: ReportValueTypeEnum;
  year?: number;
  schoolIds?: string[];
  schoolLevel?: SchoolTypeEnum;
  type?: SystemUserReportTypeEnum;
  isLegacy?: boolean;
}

// --- Executive Summary (live builder GET + saved snapshot in system_user_reports.tableData) ---

export interface IExecutiveSummaryNarrativeDriver {
  label: string;
  value: string | number;
}

export interface IExecutiveSummaryNarrativeSlot {
  body: string;
  drivers: IExecutiveSummaryNarrativeDriver[];
}

/** EC-1061 slot key (e.g. `dashboard.lead`) → narrative. Absent slot = no key. */
export type IExecutiveSummaryNarrativesMap = Record<string, IExecutiveSummaryNarrativeSlot>;

export interface IExecutiveSummaryMeta {
  districtId: string;
  districtName: string;
  startYear: number;
  yearsToPredict: number;
  isLegacy: boolean;
  mlReportRunId: string | null;
}

export interface IExecutiveSummaryTrackSeries {
  predicted: number[];
  conservative: number[];
  moderate: number[];
}

export interface IExecutiveSummaryLevelTrackSeries extends IExecutiveSummaryTrackSeries {
  level: SchoolTypeEnum;
  /** Fall-snapshot enrollment for the baseline (today) year. Null/absent when history is missing. */
  actual?: number | null;
}

export interface IExecutiveSummaryLevelSeries {
  level: SchoolTypeEnum;
  values: number[];
}

export interface IExecutiveSummaryCorridor {
  years: number[];
  forecastStartYear: number;
  actual: (number | null)[] | null;
  /** Midpoint track from reports grade.predicted; null on history years. Absent on older saved snapshots. */
  predicted?: (number | null)[];
  conservative: (number | null)[];
  moderate: (number | null)[];
}

export interface IExecutiveSummaryDashData {
  corridor: IExecutiveSummaryCorridor;
}

export interface IExecutiveSummaryGradeSeries {
  grade: string;
  values: number[];
  /** Projection tracks only: TK is an ML estimate (EC-1262). */
  isEstimate?: boolean;
}

export interface IExecutiveSummaryPctChangeCell {
  text: string;
  tone: "up" | "down" | "muted";
}

export interface IExecutiveSummaryHorizonsGradeTrack {
  track: "predicted" | "conservative" | "moderate";
  years: number[];
  grades: IExecutiveSummaryGradeSeries[];
  sdc: number[];
  totals: number[];
  /** Sum of grade rows per year (excludes SDC). Absent on older saved snapshots. */
  subtotals?: number[];
  /** YoY on subtotals; index 0 uses prior fall year when API had history. Absent on older snapshots. */
  pctChange?: IExecutiveSummaryPctChangeCell[];
  /** Projected + Estimated Total (TK–12); only when the track has a TK estimate row (EC-1262). */
  estimatedTotals?: number[];
}

export interface IExecutiveSummaryHorizonsData {
  years: number[];
  district: IExecutiveSummaryTrackSeries;
  byLevel: IExecutiveSummaryLevelTrackSeries[];
  /** District-wide grade tracks trimmed to first 5 forecast years. Absent on older saved snapshots. */
  gradeTracks?: IExecutiveSummaryHorizonsGradeTrack[];
}

export interface IExecutiveSummaryDemoData {
  years: number[];
  baselineYear: number;
  comparisonYear: number;
  grades: IExecutiveSummaryGradeSeries[];
  sdc: number[];
  totals: number[];
  kindergartenByYear: number[];
  seniorsByYear: number[];
}

export interface IExecutiveSummaryHousingTotals {
  projects: number;
  units: number;
  students: number;
}

export interface IExecutiveSummaryHousingPhasing {
  year: number;
  units: number;
  students: number;
}

export interface IExecutiveSummaryHousingProject {
  projectId: string;
  name: string;
  units: number;
  occupancyYear: number | null;
  students: number;
}

export interface IExecutiveSummaryHousingData {
  totals: IExecutiveSummaryHousingTotals;
  phasing: IExecutiveSummaryHousingPhasing[];
  notableProjects: IExecutiveSummaryHousingProject[];
}

export interface IExecutiveSummaryChoiceTrackArrays {
  actual: (number | null)[];
  predicted: (number | null)[];
  conservative: (number | null)[];
  moderate: (number | null)[];
}

export interface IExecutiveSummaryChoiceLevelCorridor extends IExecutiveSummaryChoiceTrackArrays {
  level: SchoolTypeEnum;
}

export interface IExecutiveSummaryChoiceCorridorSeries {
  district: IExecutiveSummaryChoiceTrackArrays;
  byLevel: IExecutiveSummaryChoiceLevelCorridor[];
}

export interface IExecutiveSummaryChoiceDataQuality {
  unknownShare: number;
  oodShare: number;
  warning: boolean;
}

export interface IExecutiveSummaryChoiceData {
  years: number[];
  forecastStartYear: number;
  openEnrollment: IExecutiveSummaryChoiceCorridorSeries;
  outOfDistrict: IExecutiveSummaryChoiceCorridorSeries;
  dataQuality: IExecutiveSummaryChoiceDataQuality;
}

export type ExecutiveSummaryStructuralNoteType = "closure" | "new_school" | "zone_change" | "grade_change";

export interface IExecutiveSummaryStructuralNote {
  type: ExecutiveSummaryStructuralNoteType;
  text: string;
}

export interface IExecutiveSummaryInfraHotspot {
  schoolId: string;
  name: string;
  level: SchoolTypeEnum | null;
  now: number;
  fiveYear: number;
  delta: number;
  moderateFiveYear: number;
  /** Optional: snapshots saved before structural notes existed omit it. */
  structuralNotes?: IExecutiveSummaryStructuralNote[];
}

export interface IExecutiveSummaryCapacityCampus {
  schoolId: string;
  name: string;
  level: SchoolTypeEnum | null;
  year: number;
  forecast: number;
  capacity: number;
  gap: number;
}

export interface IExecutiveSummaryCapacityMissing {
  schoolId: string;
  name: string;
  level: SchoolTypeEnum | null;
}

export interface IExecutiveSummaryCapacity {
  short: IExecutiveSummaryCapacityCampus[];
  room: IExecutiveSummaryCapacityCampus[];
  missing: IExecutiveSummaryCapacityMissing[];
}

export interface IExecutiveSummaryInfraData {
  hotspots: IExecutiveSummaryInfraHotspot[];
  capacity: IExecutiveSummaryCapacity;
}

export interface IExecutiveSummaryMlSection<TData> {
  narratives: IExecutiveSummaryNarrativesMap;
  data: TData | null;
}

export type IExecutiveSummaryDashSection = IExecutiveSummaryMlSection<IExecutiveSummaryDashData>;
export type IExecutiveSummaryDemoSection = IExecutiveSummaryMlSection<IExecutiveSummaryDemoData>;
export type IExecutiveSummaryHousingSection = IExecutiveSummaryMlSection<IExecutiveSummaryHousingData>;
export type IExecutiveSummaryHorizonsSection = IExecutiveSummaryMlSection<IExecutiveSummaryHorizonsData>;
export type IExecutiveSummaryChoiceSection = IExecutiveSummaryMlSection<IExecutiveSummaryChoiceData>;
export type IExecutiveSummaryInfraSection = IExecutiveSummaryMlSection<IExecutiveSummaryInfraData>;

/** Live builder GET — all six ML sections always present. */
export interface IExecutiveSummaryLiveSections {
  dash: IExecutiveSummaryDashSection;
  demo: IExecutiveSummaryDemoSection;
  housing: IExecutiveSummaryHousingSection;
  horizons: IExecutiveSummaryHorizonsSection;
  choice: IExecutiveSummaryChoiceSection;
  infra: IExecutiveSummaryInfraSection;
}

/** Live builder payload assembled on the client from the phase requests below. */
export interface IExecutiveSummaryReportResponse {
  meta: IExecutiveSummaryMeta;
  sections: IExecutiveSummaryLiveSections;
}

// Live builder phases: one request per section builder (`GET /report/executive-summary/<phase>`).

export interface IExecutiveSummaryMetaPhaseResponse {
  meta: IExecutiveSummaryMeta;
  narratives: Record<keyof IExecutiveSummaryLiveSections, IExecutiveSummaryNarrativesMap>;
}

/** Forecast-only horizons; by-level actuals and grade-track YoY are joined from the history phase. */
export interface IExecutiveSummaryProjectionPhaseResponse {
  data: IExecutiveSummaryHorizonsData;
}

export interface IExecutiveSummaryLevelHistorySeries {
  level: SchoolTypeEnum;
  years: number[];
  totals: number[];
}

export interface IExecutiveSummaryHistoryPhaseResponse {
  demo: IExecutiveSummaryDemoData | null;
  historyYears: number[];
  historyTotals: number[];
  byLevelHistory: IExecutiveSummaryLevelHistorySeries[];
}

export interface IExecutiveSummaryHousingPhaseResponse {
  data: IExecutiveSummaryHousingData | null;
}

export interface IExecutiveSummaryChoicePhaseResponse {
  data: IExecutiveSummaryChoiceData | null;
}

export interface IExecutiveSummaryInfraPhaseResponse {
  data: IExecutiveSummaryInfraData | null;
}

export interface IExecutiveSummaryPhases {
  meta: IExecutiveSummaryMetaPhaseResponse;
  projection: IExecutiveSummaryProjectionPhaseResponse;
  history: IExecutiveSummaryHistoryPhaseResponse;
  housing: IExecutiveSummaryHousingPhaseResponse;
  choice: IExecutiveSummaryChoicePhaseResponse;
  infra: IExecutiveSummaryInfraPhaseResponse;
}

export interface IGetExecutiveSummaryReportArg {
  districtId: string;
  year?: number;
  isLegacy?: boolean;
}

/** API itemKey for Executive Summary projection-review events. */
export type ExecutiveSummaryProjectionReviewItemKey =
  | "all_schools_conservative"
  | "all_schools_moderate"
  | "district_wide_conservative"
  | "district_wide_moderate";

export type ExecutiveSummaryProjectionReviewAction = "CONFIRMED" | "SKIPPED";

export type ExecutiveSummaryProjectionReviewItemStatus =
  | "PENDING"
  | "CONFIRMED"
  | "SKIPPED";

export interface IExecutiveSummaryProjectionReviewScope {
  districtId: string;
  year: number;
  isLegacy: boolean;
  mlReportRunId: string | null;
}

export interface IExecutiveSummaryProjectionReviewItem {
  itemKey: ExecutiveSummaryProjectionReviewItemKey;
  status: ExecutiveSummaryProjectionReviewItemStatus;
  lastActionAt: string | null;
  lastActionByUserId: string | null;
}

export interface IExecutiveSummaryProjectionReviewHistoryItem {
  id: string;
  itemKey: string;
  action: ExecutiveSummaryProjectionReviewAction;
  userId: string;
  createdAt: string;
}

export interface IExecutiveSummaryProjectionReviewStatusResponse {
  scope: IExecutiveSummaryProjectionReviewScope;
  items: IExecutiveSummaryProjectionReviewItem[];
  canAccessBuilder: boolean;
  canSkip: boolean;
  history: IExecutiveSummaryProjectionReviewHistoryItem[];
}

export type IGetExecutiveSummaryProjectionReviewArg = IGetExecutiveSummaryReportArg;

export interface IRecordExecutiveSummaryProjectionReviewRequest {
  districtId: string;
  year?: number;
  isLegacy?: boolean;
  itemKey: ExecutiveSummaryProjectionReviewItemKey;
  action: ExecutiveSummaryProjectionReviewAction;
}

export interface ISkipAllExecutiveSummaryProjectionReviewRequest {
  districtId: string;
  year?: number;
  isLegacy?: boolean;
}

/** Front-only cover content frozen at save time. */
export interface IExecutiveSummaryCoverSection {
  released: string;
}

export interface IExecutiveSummaryLetterSignOff {
  regards: string;
  name: string;
  title: string;
}

export interface IExecutiveSummaryLetterSection {
  paragraphs: string[];
  signOff: IExecutiveSummaryLetterSignOff;
}

export interface IExecutiveSummaryAppendixMethodStep {
  n: string;
  title: string;
  desc: string;
}

export interface IExecutiveSummaryAppendixGuardNote {
  label: string;
  text: string;
}

export interface IExecutiveSummaryAppendixSection {
  methodIntro: string;
  methodSteps: IExecutiveSummaryAppendixMethodStep[];
  methodOutro: string;
  guardNotes: IExecutiveSummaryAppendixGuardNote[];
}

/**
 * Sections in a saved ES snapshot — only included keys are stored.
 * ML sections match GET shape; cover / letter / appendix are client-built.
 */
export interface IExecutiveSummarySavedSections {
  cover?: IExecutiveSummaryCoverSection;
  letter?: IExecutiveSummaryLetterSection;
  dash?: IExecutiveSummaryDashSection;
  demo?: IExecutiveSummaryDemoSection;
  housing?: IExecutiveSummaryHousingSection;
  horizons?: IExecutiveSummaryHorizonsSection;
  choice?: IExecutiveSummaryChoiceSection;
  infra?: IExecutiveSummaryInfraSection;
  appendix?: IExecutiveSummaryAppendixSection;
}

/** Persisted `tableData` for SystemUserReport type EXECUTIVE_SUMMARY. */
export interface IExecutiveSummarySavedSnapshot {
  meta: IExecutiveSummaryMeta;
  includeCapacity: boolean;
  salutation: string;
  presetName: string;
  sections: IExecutiveSummarySavedSections;
}

export interface ISaveExecutiveSummaryReportRequest {
  districtId: string;
  year?: number;
  isLegacy?: boolean;
  name?: string;
  includeCapacity: boolean;
  salutation: string;
  presetName: string;
  meta: IExecutiveSummaryMeta;
  sections: IExecutiveSummarySavedSections;
}

/** `system_user_reports.tableData` — projection tables or ES snapshot (other types cast at use site). */
export type ISystemUserReportTableData = IProjectionTableData[] | IExecutiveSummarySavedSnapshot;

export interface IOutOfDistrictByYearRowData {
  schoolId?: string;
  schoolName: string;
  values: number[];
  yearsClosedInRange?: number;
}

export interface IOutOfDistrictByYearTableData {
  year: number;
  gradeOrder: string[];
  rows: IOutOfDistrictByYearRowData[];
  totals: number[];
}

export interface ISystemUserReportListItem {
  id: string;
  name: string;
  valueType: ReportValueTypeEnum;
  reportType: ProjectionTypeEnum;
  type: SystemUserReportTypeEnum;
  year?: number;
  desc?: string | null;
  isHidden: boolean;
  isLegacy: boolean;
  /** PRIVATE = owner only; SHARED = visible to district members */
  visibility: "PRIVATE" | "SHARED";
  /** Present on shared / combined list scopes when author can be resolved */
  authorName?: string | null;
  projectionModel?: IProjectionModelMetadata | null;
  /** Forecast year span for projection reports (e.g. "2026-2035"). */
  projectionScope?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ISystemUserReportsResponse {
  reports: ISystemUserReportListItem[];
}

/** List scope for saved system reports (EnrollCast). Default on API: combined. */
export type SavedSystemUserReportScope = "private" | "shared" | "combined";

export interface ISystemUserReportResponse {
  id: string;
  districtId: string;
  userId: string;
  name: string;
  valueType: ReportValueTypeEnum;
  reportType: ProjectionTypeEnum;
  type: SystemUserReportTypeEnum;
  tableData: ISystemUserReportTableData;
  desc?: string | null;
  isHidden: boolean;
  isLegacy: boolean;
  visibility: "PRIVATE" | "SHARED";
  projectionModel?: IProjectionModelMetadata | null;
  /** Forecast year span for projection reports (e.g. "2026-2035"). */
  projectionScope?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IProjectionFeatureStatusResponse {
  feature: "PROJECTION_REPORTS";
  enabled: boolean;
}

export interface IAiReportRowMetadata {
  schoolId?: string;
}

export type IAiReportRow = Record<string, string> & {
  metadata?: IAiReportRowMetadata;
};

export interface IAiReportResponse {
  id: string;
  districtId: string | null;
  schoolId: string | null;
  startYear: number;
  yearsToPredict: number;
  type: string;
  data: {
    schema: { name: string }[];
    rows: IAiReportRow[];
    /** Present when the stored snapshot was capped (chat still shows a short preview). */
    truncated?: boolean;
    previewRowCount?: number;
    storedRowCount?: number;
    total?: number;
  };
  authorId: string | null;
  authorName: string | null;
  reportJobId: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Full result set of an AI report (up to the export cap), used for "Download as CSV". */
export interface IAiReportExportResponse {
  schema: { name: string; label?: string }[];
  rows: IAiReportRow[];
  total: number;
}

// --- Custom reports (user-created reports, e.g. saved AI reports; separate from system/Enrollcast reports) ---

/** Scope for listing custom reports: mine = My Reports, district = District Reports (shared) */
export type CustomReportScope = "mine" | "district";

/** API response for a custom report. Visibility controls My Reports (private) vs District Reports (shared). */
export interface ICustomReportResponse {
  id: string;
  districtId: string;
  userId: string;
  name: string;
  description: string | null;
  filters: unknown;
  visibility: "PRIVATE" | "SHARED";
  reportId: string | null;
  type?: string | null;
  authorName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type SchoolTypeEnum = "ELEMENTARY" | "MIDDLE" | "HIGH" | "K8";

export interface ISchoolLevelInfo {
  id: string;
  type: SchoolTypeEnum;
  lowestGrade: number;
  highestGrade: number;
}

export interface ISchoolInfo {
  id: string;
  name: string;
  internalId: string;
  schoolLevels: ISchoolLevelInfo[];
}

export interface ISchoolsResponse {
  schools: ISchoolInfo[];
}

export type DistrictDataProcessingStage =
  | "import"
  | "edlink_sync"
  | "geocoding"
  | "geofencing"
  | "school_list_deletion";

export interface ICheckDataSufficiencyResponse {
  hasEnoughData: boolean;
  reasons?: string[];
  hasNewHousingData: boolean;
  projectionBaselineYear?: number;
  /** District data still processing; report generation / ML training can't start until empty. */
  dataProcessingStages?: DistrictDataProcessingStage[];
}

export interface IProjectionBaselineYearsResponse {
  reportStartYears: number[];
}

export interface IProjectionAvailability {
  type: ProjectionTypeEnum;
  available: boolean;
}

export interface IAvailableProjectionsResponse {
  projections: IProjectionAvailability[];
}

export interface IAvailableHistoricalEnrollmentResponse {
  historicalEnrollment: IProjectionAvailability[];
}

export interface IOpenEnrollmentData {
  columns: string[];
  columnSchoolIds?: string[];
  data: {
    attendanceArea: string;
    values: number[];
  }[];
  subtotals: number[];
  outOfDistrict: number[];
  totals: number[];
}

export interface IOpenEnrollmentReportResponse {
  data: IOpenEnrollmentData;
}

export interface INewHousingProjectsReportResponse {
  columns: string[];
  rows: { data: Record<string, string | number> }[];
}

export interface IStudentListPreviewFiltersRequest {
  districtId: string;
  districtWide?: boolean;
  shapeIds?: string[];
  boundaryIds?: string[];
  nationalBoundariesIds?: string[];
  previewIds?: string[];
  customLayerBoundaryIds?: string[];
  mapLevelBoundaryIds?: string[];
  geometries?: (Polygon | MultiPolygon)[];
  clusterGrid?: IClusterGridKey;
  schoolType?: string;
  attendingSchoolIds?: string[];
  year: number;
  snapshotType: StudentImportType;
  month?: number;
  colorBy?: StudentCountsInShapeColorByEnum;
  gradeFrom?: number;
  gradeTo?: number;
  customAttributeFilters?: Record<string, string[]>;
  advancedFilters?: IAdvancedFilter[];
}

export interface IStudentListPreviewRequest extends IStudentListPreviewFiltersRequest {
  visibleSchoolTypes?: VisibleSchoolTypesInShape;
}

export interface IExportStudentListReportRequest extends IStudentListPreviewRequest {
  format: ReportExportFormat;
  title?: string;
}

export interface IStudentListPreviewResponse {
  studentCounts: IStudentCountsInShapeResult;
  schoolCounts: ISchoolCountsInShapeResult;
}

export interface IStudentListPreviewStudentsRequest extends IStudentListPreviewFiltersRequest {
  cursor?: string;
  limit?: number;
}

export interface IStudentListPreviewRow {
  id: string;
  studentId: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  latitude?: number;
  longitude?: number;
  gradeCode?: string;
  gradeLabel?: string;
  attendingSchoolId?: string;
  attendingSchoolInternalId?: string;
  attendingSchoolName?: string;
  sourceCreatedAt: string;
}

export interface IStudentListPreviewStudentsResponse {
  items: IStudentListPreviewRow[];
  nextCursor: string | null;
  total: number;
}

export interface IGenerateStudentListReportResponse {
  reportId: string;
}

export type StudentListReportMaterializationStatus = "pending" | "complete" | "failed";

export interface IStudentListReportMaterialization {
  status: StudentListReportMaterializationStatus;
  startedAt?: string;
  completedAt?: string;
  error?: string | null;
  rowCount?: number;
}

export interface IStudentListReportDataV1 {
  version: 1;
  studentCountsParams: IStudentListPreviewFiltersRequest;
  schoolCountsParams: Record<string, unknown>;
  studentCounts: IStudentCountsInShapeResult;
  schoolCounts: ISchoolCountsInShapeResult;
  materialization: IStudentListReportMaterialization;
}

export interface IStudentListReportResponse {
  id: string;
  districtId: string | null;
  schoolId: string | null;
  startYear: number;
  yearsToPredict: number;
  type: string;
  data: IStudentListReportDataV1;
  authorId: string | null;
  authorName: string | null;
  reportJobId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IGetStudentListReportStudentsArg {
  reportId: string;
  cursor?: string;
  limit?: number;
}

export interface IStudentListReportStudentsResponse {
  items: IStudentListPreviewRow[];
  nextCursor: string | null;
  pending?: boolean;
}

export const STUDENT_LIST_REPORT_TYPE = "StudentListReport";
